import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  adminApi,
  ConfigRow,
  PipelineReconcileResult,
  QdrantMigrateResult,
  QdrantRechunkAllResult,
} from '../api/admin';
import AdminConfigEditor from '../components/AdminConfigEditor';
import AdminTabsNav from '../components/AdminTabsNav';
import ConfigValuePreview from '../components/ConfigValuePreview';
import { HelpTipButton } from '../components/HelpTipButton';
import { getConfigGroupLabel, getConfigKeyHelp } from '../utils/adminConfigDescriptions';
import { useIsMobileViewport } from '../utils/useIsMobileViewport';
import './AdminConfig.css';

type CatFilter = 'all' | 'prompts' | 'responses' | 'retrieval' | 'maps' | 'other';

/** A logical card on the AdminConfig page: every system_config row whose
 * key shares the same `<category>.<group>` prefix is stacked under one
 * card so prompts + responses that participate in the same request are
 * edited side-by-side. */
interface ConfigGroup {
  /** `<category>.<group>` — stable identity for React keys and search. */
  id: string;
  /** First segment, e.g. `prompts`. Used by the category filter. */
  category: string;
  /** Middle segment, e.g. `classification`. Drives the human label. */
  group: string;
  /** Human label from `CONFIG_GROUP_LABELS` (fallback: title-cased segment). */
  label: string;
  rows: ConfigRow[];
}

/**
 * Group rows for card rendering.
 *
 * Rule: every key uses the nested `<category>.<group>.<leaf>` form
 * (e.g. `prompts.classification.system`). We bucket by the
 * `<category>.<group>` prefix so all related rows (system + user, or
 * the various summary phases) share one card. For two-segment keys
 * (e.g. `retrieval.weights`) the group IS the leaf — they end up
 * alone in their own card, which is exactly what the user sees today.
 */
function buildConfigGroups(rows: ConfigRow[]): ConfigGroup[] {
  const buckets = new Map<string, ConfigGroup>();
  for (const row of rows) {
    const parts = row.key.split('.');
    const category = parts[0] ?? row.category ?? 'other';
    // `<group>` segment: middle for 3+ part keys, leaf otherwise.
    const group = parts.length >= 2 ? parts[1] : '_';
    const id = `${category}.${group}`;
    let bucket = buckets.get(id);
    if (!bucket) {
      bucket = {
        id,
        category,
        group,
        label: getConfigGroupLabel(group),
        rows: [],
      };
      buckets.set(id, bucket);
    }
    bucket.rows.push(row);
  }
  // Stable order: by id alphabetical (so prompts.* cluster together,
  // responses.* next, etc.). Rows within a group keep DB sort order.
  return Array.from(buckets.values()).sort((a, b) => a.id.localeCompare(b.id));
}

const AdminConfig: React.FC = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<ConfigRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [seedMsg, setSeedMsg] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [editRow, setEditRow] = useState<ConfigRow | null>(null);
  const [query, setQuery] = useState('');
  const [catFilter, setCatFilter] = useState<CatFilter>('all');
  const [qdrantMsg, setQdrantMsg] = useState('');
  const [pipelineBusy, setPipelineBusy] = useState(false);
  const isMobile = useIsMobileViewport();

  const load = async () => {
    setRows(await adminApi.listConfig());
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    (async () => {
      try {
        await load();
      } catch (e: unknown) {
        const err = e as { response?: { data?: { detail?: string } } };
        setError(err.response?.data?.detail || 'Nu s-a putut încărca configurația');
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const categories = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => {
      if (r.category) s.add(r.category);
    });
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      const c = (r.category || '').toLowerCase();
      if (catFilter !== 'all') {
        if (catFilter === 'other') {
          if (c === 'prompts' || c === 'responses' || c === 'retrieval' || c === 'maps') return false;
        } else if (c !== catFilter) {
          return false;
        }
      }
      if (!q) return true;
      return r.key.toLowerCase().includes(q) || c.includes(q);
    });
  }, [rows, query, catFilter]);

  // Card-level grouping happens AFTER per-row filtering so search hits
  // a single key still surface that whole group (with only the matching
  // rows inside) instead of dropping the group entirely.
  const groups = useMemo(() => buildConfigGroups(filtered), [filtered]);

  const seed = async () => {
    setSeedMsg('');
    try {
      const r = await adminApi.seedDefaults();
      setSeedMsg(`Inserate ${r.seeded} chei noi.`);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Eroare seed');
    }
  };

  const wipeAndSeed = async () => {
    const ok = window.confirm(
      'ȘTERGE TOATE cheile de configurație și apoi încarcă valorile implicite din cod?\n\n' +
        'Folosit după redenumiri non-aditive de chei: rândurile vechi sunt șterse, ' +
        'iar cele noi sunt inserate într-o singură operație.\n' +
        'Această acțiune nu poate fi anulată.'
    );
    if (!ok) return;
    setError('');
    setSaveMsg('');
    setSeedMsg('');
    try {
      const r = await adminApi.seedDefaults({ wipe: true });
      setSeedMsg(`Re-inițializat: ${r.seeded} chei inserate după ștergere.`);
      if (editRow) setEditRow(null);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Eroare la wipe + seed');
    }
  };

  const deleteAll = async () => {
    const ok = window.confirm(
      'Ștergeți TOATE cheile de configurație din baza de date?\n\n' +
        'Aplicația va reveni la valorile implicite din cod.\n' +
        'Această acțiune nu poate fi anulată.'
    );
    if (!ok) return;
    setError('');
    setSaveMsg('');
    setSeedMsg('');
    try {
      const r = await adminApi.deleteAllConfig();
      setSaveMsg(`Șterse ${r.deleted} intrări.`);
      if (editRow) setEditRow(null);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Eroare la ștergerea tuturor cheilor');
    }
  };

  const qdrantDryRun = async () => {
    setError('');
    setQdrantMsg('');
    try {
      const r: QdrantMigrateResult = await adminApi.qdrantMigrateLangchainPayload({ dry_run: true, batch_size: 256 });
      setQdrantMsg(
        `Qdrant (dry-run): collection=${r.collection} scanned=${r.scanned} would_update=${r.updated} skipped=${r.skipped}`
      );
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Eroare la dry-run Qdrant');
    }
  };

  const qdrantRun = async () => {
    const ok = window.confirm(
      'Rulați migrarea Qdrant -> format LangChain?\n\n' +
        'Aceasta va adăuga câmpurile payload `page_content` și `metadata` pentru punctele existente.\n' +
        'Operația poate dura câteva minute.'
    );
    if (!ok) return;
    setError('');
    setQdrantMsg('');
    try {
      const r: QdrantMigrateResult = await adminApi.qdrantMigrateLangchainPayload({ dry_run: false, batch_size: 256 });
      setQdrantMsg(
        `Qdrant: collection=${r.collection} scanned=${r.scanned} updated=${r.updated} skipped=${r.skipped}`
      );
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Eroare la migrarea Qdrant');
    }
  };

  const qdrantRechunkDryRun = async () => {
    setError('');
    setQdrantMsg('');
    try {
      const r: QdrantRechunkAllResult = await adminApi.qdrantRechunkAll({ dry_run: true, limit: 0, offset: 0 });
      setQdrantMsg(
        `Qdrant rechunk (dry-run): documents=${r.documents_total} would_process=${r.processed} skipped=${r.skipped}`
      );
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Eroare la dry-run rechunk');
    }
  };

  const qdrantRechunkRun = async () => {
    const ok = window.confirm(
      'Re-chunk + re-embed pentru TOATE documentele în Qdrant?\n\n' +
        'Aceasta va șterge și recrea chunks per document (în Qdrant) folosind noul chunker.\n' +
        'Poate dura câteva minute.'
    );
    if (!ok) return;
    setError('');
    setQdrantMsg('');
    try {
      const r: QdrantRechunkAllResult = await adminApi.qdrantRechunkAll({ dry_run: false, limit: 0, offset: 0 });
      const errCount = (r.errors || []).length;
      setQdrantMsg(
        `Qdrant rechunk: documents=${r.documents_total} processed=${r.processed} skipped=${r.skipped} errors=${errCount}`
      );
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Eroare la rechunk');
    }
  };

  // Force-reset OCR / summary / classification rows stuck in transient
  // states (`processing`, `generating`, etc.) — typically after a worker
  // or OCR-service restart left documents mid-flight. The backend janitor
  // emits SSE events so any open dossier dismisses its spinner.
  const pipelineReconcile = async () => {
    const ok = window.confirm(
      'Resetează documentele blocate în procesare?\n\n' +
        'Toate rândurile aflate într-o stare tranzitorie (`processing`, `generating`, etc.) ' +
        'vor fi marcate ca eroare, indiferent de vechime. Folosiți doar după un restart ' +
        'planificat al worker-ului / serviciului OCR, când sunteți sigur că nu rulează ' +
        'nici o sarcină în fundal — altfel sarcinile în curs vor fi întrerupte.'
    );
    if (!ok) return;
    setError('');
    setQdrantMsg('');
    setPipelineBusy(true);
    try {
      const r: PipelineReconcileResult = await adminApi.pipelineReconcile(0);
      const total = r.ocr_reset + r.summary_reset + r.classification_reset;
      if (total === 0) {
        setQdrantMsg('Pipeline: niciun document blocat — nimic de resetat.');
      } else {
        setQdrantMsg(
          `Pipeline resetat: OCR=${r.ocr_reset}, rezumate=${r.summary_reset}, clasificări=${r.classification_reset}.`
        );
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Eroare la resetarea pipeline-ului');
    } finally {
      setPipelineBusy(false);
    }
  };

  const saveEdit = async (value: Record<string, unknown>) => {
    if (!editRow) return;
    setSaveMsg('');
    try {
      await adminApi.putConfig(editRow.key, value);
      setSaveMsg('Salvat.');
      setEditRow(null);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const deleteRow = async (r: ConfigRow) => {
    const ok = window.confirm(
      `Șterge definitiv configurația „${r.key}” din baza de date?\n\nAplicația va folosi valorile implicite din cod pentru această cheie.`
    );
    if (!ok) return;
    setError('');
    setSaveMsg('');
    try {
      await adminApi.deleteConfig(r.key);
      if (editRow?.key === r.key) setEditRow(null);
      setSaveMsg('Șters.');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Eroare la ștergere');
    }
  };

  const doExport = async () => {
    setError('');
    try {
      const data = await adminApi.exportConfig();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `edosar-config-export-v${data.version}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Export eșuat');
    }
  };

  const onImportFile = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const f = ev.target.files?.[0];
    ev.target.value = '';
    if (!f) return;
    setError('');
    try {
      const text = await f.text();
      const data = JSON.parse(text) as { items?: ConfigRow[] };
      if (!data.items || !Array.isArray(data.items)) {
        setError('Fișier invalid: lipsește items[]');
        return;
      }
      const r = await adminApi.importConfig(data.items, false);
      setSeedMsg(`Importate ${r.imported} chei.`);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Import eșuat');
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="page-container">
        <p className="alert alert-error">Acces interzis.</p>
        <Link to="/dashboard">Înapoi</Link>
      </div>
    );
  }

  return (
    <div className="page-container admin-page">
      <nav className="breadcrumbs admin-breadcrumbs">
        <Link to="/dashboard">Dashboard</Link>
        <span className="admin-bc-sep">/</span>
        <span>Administrare</span>
        <span className="admin-bc-sep">/</span>
        <span>Sistem</span>
      </nav>

      <AdminTabsNav />

      <header className="admin-hero">
        <div className="admin-hero-text">
          <h1 className="page-title admin-hero-title">Sistem</h1>
          <p className="page-subtitle admin-hero-sub">
            Prompturi LLM, mesaje chat (răspunsuri), ponderi căutare și mapări — în baza de date. Editați în formular sau JSON.
          </p>
        </div>
        <div className="admin-hero-actions">
          <button type="button" className="btn btn-secondary" onClick={seed}>
            Valori implicite
          </button>
          <button type="button" className="btn btn-secondary" onClick={wipeAndSeed}>
            Wipe + seed
          </button>
          <button type="button" className="btn btn-secondary" onClick={qdrantDryRun}>
            Qdrant dry-run
          </button>
          <button type="button" className="btn btn-secondary" onClick={qdrantRun}>
            Migrează Qdrant
          </button>
          <button type="button" className="btn btn-secondary" onClick={qdrantRechunkDryRun}>
            Rechunk dry-run
          </button>
          <button type="button" className="btn btn-secondary" onClick={qdrantRechunkRun}>
            Rechunk toate
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={pipelineReconcile}
            disabled={pipelineBusy}
            title="Marchează ca eroare documentele blocate în procesare (`processing` / `generating`). Folosiți după un restart al worker-ului sau al serviciului OCR."
          >
            {pipelineBusy ? 'Se resetează…' : 'Resetează blocate'}
          </button>
          <button type="button" className="btn btn-danger btn-sm" onClick={deleteAll}>
            Șterge tot
          </button>
          <button type="button" className="btn btn-secondary" onClick={doExport}>
            Export JSON
          </button>
          <label className="btn btn-secondary admin-file-btn">
            Import JSON
            <input type="file" accept="application/json" className="admin-file-input" onChange={onImportFile} />
          </label>
        </div>
      </header>

      {error && (
        <div className="admin-flash admin-flash--error" role="alert">
          <span className="admin-flash__icon" aria-hidden>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </span>
          <span className="admin-flash__text">{error}</span>
        </div>
      )}

      {qdrantMsg && (
        <div className="admin-flash admin-flash--ok" role="status">
          <span className="admin-flash__text">{qdrantMsg}</span>
        </div>
      )}
      {seedMsg && (
        <div className="admin-flash admin-flash--info" role="status">
          <span className="admin-flash__icon" aria-hidden>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </span>
          <span className="admin-flash__text">{seedMsg}</span>
        </div>
      )}
      {saveMsg && (
        <div className="admin-flash admin-flash--success" role="status">
          <span className="admin-flash__icon" aria-hidden>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          <span className="admin-flash__text">{saveMsg}</span>
        </div>
      )}

      <details
        className="admin-toolbar-panel admin-toolbar-collapsible"
        open={!isMobile}
        aria-label="Filtrare listă configurație"
      >
        <summary className="admin-toolbar-summary">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="7" y1="12" x2="17" y2="12" />
            <line x1="10" y1="18" x2="14" y2="18" />
          </svg>
          <span>Filtre {catFilter !== 'all' || query ? <em className="admin-toolbar-summary__active">(active)</em> : null}</span>
          <svg className="admin-toolbar-summary__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </summary>
        <div className="admin-toolbar-panel__row">
          <div className="admin-toolbar-search-block">
            <span className="admin-toolbar-filters-label" id="admin-search-label">
              Caută
            </span>
            <div className="admin-search-wrap">
              <svg className="admin-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="search"
                className="input admin-search"
                placeholder="Cheie sau categorie…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-labelledby="admin-search-label"
              />
            </div>
          </div>
          <div className="admin-toolbar-filters-block">
            <span className="admin-toolbar-filters-label" id="admin-cat-label">
              Categorie
            </span>
            <div className="admin-filters" role="group" aria-labelledby="admin-cat-label">
              {(
                [
                  ['all', 'Toate'],
                  ['prompts', 'Prompturi'],
                  ['responses', 'Răspunsuri'],
                  ['retrieval', 'Căutare'],
                  ['maps', 'Mapări'],
                  ['other', 'Altele'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={`admin-filter-chip${catFilter === id ? ' active' : ''}`}
                  onClick={() => setCatFilter(id)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </details>

      {categories.length > 0 && (
        <div className="admin-stats" aria-live="polite">
          <span className="admin-stats__badge">
            <strong>{rows.length}</strong> intrări în total
          </span>
          {filtered.length !== rows.length && (
            <span className="admin-stats__badge admin-stats__badge--muted">
              <strong>{filtered.length}</strong> după filtru
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="admin-skeleton-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card admin-skeleton-card" />
          ))}
        </div>
      ) : (
        <div className="admin-grid">
          {groups.map((g) => (
            <article key={g.id} className="card admin-card admin-card--group">
              <header className="admin-card-head admin-card-head--group">
                <div className="admin-card-group-title">
                  <h2 className="admin-card-group-label">{g.label}</h2>
                  <code className="admin-card-key admin-card-key--group">{g.id}</code>
                </div>
                <div className="admin-card-head__meta">
                  <span className="admin-card-cat">{g.category}</span>
                </div>
              </header>
              <div className="admin-card-rows">
                {g.rows.map((r) => {
                  const help = getConfigKeyHelp(r.key);
                  const parts = r.key.split('.');
                  const leaf = parts.length >= 3 ? parts.slice(2).join('.') : parts[parts.length - 1];
                  return (
                    <section key={r.key} className="admin-card-row">
                      <div className="admin-card-row-head">
                        <span className="admin-card-row-leaf">{leaf}</span>
                        <code className="admin-card-row-key">{r.key}</code>
                        <HelpTipButton
                          content={help.long}
                          ariaLabel={`Descriere: ${help.short}`}
                          buttonClassName="admin-card-tip"
                        />
                      </div>
                      <p className="admin-card-desc">{help.short}</p>
                      <ConfigValuePreview row={r} />
                      <div className="admin-card-actions">
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => setEditRow(r)}
                        >
                          Editează
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => deleteRow(r)}
                        >
                          Șterge
                        </button>
                      </div>
                    </section>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      )}

      {!loading && groups.length === 0 && (
        <div className="card admin-empty">
          <p>Nicio intrare nu se potrivește filtrului.</p>
          {rows.length === 0 && (
            <button type="button" className="btn btn-primary" onClick={seed}>
              Încarcă valori implicite
            </button>
          )}
        </div>
      )}

      {editRow && (
        <AdminConfigEditor
          row={editRow}
          onSave={saveEdit}
          onCancel={() => setEditRow(null)}
          onDelete={() => editRow && deleteRow(editRow)}
        />
      )}
    </div>
  );
};

export default AdminConfig;
